import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { computeCheckInBreakdown } from 'shared/utils/check-in-breakdown';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import { EVENTS, SCHEDULE_REPORT_EXPORT_TYPE, SCHEDULE_ANALYTICS_TAB } from 'shared/constants';
import { Button } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useEducationalEventsContext } from '../../educational-events-context';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { exportAttendees } from '../../helpers/export-attendees';
import { buildReportExportedProps } from '../../helpers/report-exported-props';
import { CheckedInList } from '../check-in/checked-in-list';
import { ManualCheckIn } from '../check-in/manual-check-in';
import styles from './event-detail-panel.module.scss';

export function CheckedInCard({ event, isOpen, canManageCheckIn, items, users }) {
  const { lang } = useEducationalEventsContext();
  const attendeesLang = useSelector(getLang('CHECKED_IN_ATTENDEES'));
  const { getValueFromLang } = useProfileLang();
  const { formatDateWithTime } = useEducationalEventFormat();
  const [gridApi, setGridApi] = useState(null);

  // Only attendees who resolve to a known org user are shown; the count and
  // breakdown derive from the same filtered list so they can't disagree.
  const usersById = useMemo(() => Object.fromEntries(users.map(user => [user.id, user])), [users]);
  const visibleItems = useMemo(
    () => items.filter(item => usersById[item.userId]),
    [items, usersById],
  );

  const total = visibleItems.length;
  const hasAttendees = total > 0;

  const breakdown = useMemo(() => {
    const result = computeCheckInBreakdown(visibleItems, total);
    if (!result) return null;
    return attendeesLang.BREAKDOWN.replace('{inPerson}', formatNumber(result.inPerson)).replace(
      '{virtual}',
      formatNumber(result.virtual),
    );
  }, [visibleItems, total, attendeesLang.BREAKDOWN]);

  const handleExport = useCallback(() => {
    if (!gridApi) {
      message.error(attendeesLang.EXPORT_FAILED);
      return;
    }
    try {
      exportAttendees({
        gridApi,
        fileName: `${event?.title} - ${attendeesLang.EXPORT_LIST}`,
        attendeesLang,
        eventsLang: lang,
        getValueFromLang,
        formatCheckInTime: formatDateWithTime,
      });
      analyticsService.track(
        EVENTS.SCHEDULE.REPORT_EXPORTED,
        buildReportExportedProps({
          reportType: SCHEDULE_REPORT_EXPORT_TYPE.EVENT_ATTENDEES,
          tab: SCHEDULE_ANALYTICS_TAB.EVENTS,
          eventId: event?.id,
        }),
      );
    } catch (error) {
      monitorService.error('Failed to export attendee list', { error });
      message.error(attendeesLang.EXPORT_FAILED);
    }
  }, [gridApi, attendeesLang, lang, getValueFromLang, formatDateWithTime, event]);

  return (
    <section className={styles.card}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeading}>
          <h3 className={styles.sectionTitle}>
            {isOpen || hasAttendees
              ? attendeesLang.HEADER.replace('{count}', formatNumber(total))
              : lang.CHECKED_IN}
          </h3>
          {!isOpen && !hasAttendees && (
            <span className={styles.sectionHint}>{lang.CHECKED_IN_HINT}</span>
          )}
          {isOpen && !hasAttendees && (
            <span className={styles.sectionHint}>{attendeesLang.EMPTY_TITLE}</span>
          )}
          {breakdown && <span className={styles.checkInBreakdown}>{breakdown}</span>}
        </div>
        {(hasAttendees || canManageCheckIn) && (
          <div className={styles.sectionActions}>
            {hasAttendees && (
              <Button size="small" onClick={handleExport}>
                {attendeesLang.EXPORT_LIST}
              </Button>
            )}
            {canManageCheckIn && (
              <ManualCheckIn
                eventId={event.id}
                users={users}
                checkedInUserIds={visibleItems.map(item => item.userId)}
              />
            )}
          </div>
        )}
      </div>
      {hasAttendees && (
        <CheckedInList items={visibleItems} users={users} onGridReady={setGridApi} />
      )}
    </section>
  );
}
